import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdAdd, MdCampaign, MdContentCopy, MdDeleteForever, MdMarkEmailRead, MdPause, MdPlayArrow, MdSchedule, MdSearch, MdSend, MdStop, MdVisibility } from 'react-icons/md';
import { getData, postData, deleteData } from '../../../Webservice/Webservice';
import EmailForm from './EmailForm';
import { FONT, Progress, STATUS, StatusChip, authToken, fmtDateTime } from './emailShared';

const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });

function Email() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [copySource, setCopySource] = useState(null); // campaign being duplicated
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: 'stop' | 'delete', campaign }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getData('campaign/email/status', null, authToken(), false);
      setCampaigns(Array.isArray(res) ? res : []);
      setError('');
    } catch (err) {
      if (!quiet) { setCampaigns([]); setError('Failed to load email campaigns. Please try again.'); }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // keep numbers fresh while something is going out
  const live = campaigns.some((c) => ['running', 'pending'].includes(c.status));
  useEffect(() => {
    if (!live || showForm) return undefined;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [live, showForm, load]);

  // open the new-campaign form pre-filled with an existing campaign's details
  const duplicate = useCallback(async (id) => {
    try {
      const res = await getData(`campaign/email/${id}`, { limit: 1 }, authToken(), false);
      if (!res?.campaign) throw new Error('missing');
      setCopySource(res.campaign);
      setShowForm(true);
    } catch (e) {
      setError('Could not load that campaign to duplicate it.');
    }
  }, []);

  // arriving from a campaign's detail page with "Duplicate" pressed
  useEffect(() => {
    const id = location.state?.duplicateId;
    if (id) {
      navigate(location.pathname, { replace: true, state: null });
      duplicate(id);
    }
  }, [location.state, location.pathname, navigate, duplicate]);

  const act = async (campaign, action) => {
    setBusy(true);
    try {
      await postData(`campaign/email/${campaign._id}/${action}`, {}, authToken(), false);
      await load(true);
    } catch (e) {
      /* the service already showed the reason */
    } finally {
      setBusy(false);
    }
  };

  const doConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'stop') await postData(`campaign/email/${confirm.campaign._id}/stop`, {}, authToken(), false);
      else await deleteData(`campaign/email/${confirm.campaign._id}`, authToken());
      await load(true);
    } catch (e) {
      /* shown by the service */
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const kpis = useMemo(() => {
    const sum = (k) => campaigns.reduce((a, c) => a + (c.stats?.[k] || 0), 0);
    return [
      { label: 'Campaigns', value: campaigns.length, icon: MdCampaign, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', filter: '' },
      { label: 'Sending now', value: campaigns.filter((c) => c.status === 'running').length, icon: MdSend, gradient: 'linear-gradient(135deg,#10b981,#059669)', filter: 'running' },
      { label: 'Scheduled', value: campaigns.filter((c) => c.status === 'scheduled').length, icon: MdSchedule, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', filter: 'scheduled' },
      { label: 'Emails delivered', value: sum('sent').toLocaleString('en-IN'), icon: MdMarkEmailRead, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', filter: 'completed' },
    ];
  }, [campaigns]);

  const rows = campaigns.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    return !q || `${c.name} ${c.subject} ${c.campId || ''}`.toLowerCase().includes(q);
  });

  if (showForm) {
    return <EmailForm key={copySource?._id || 'new'} source={copySource} onBack={() => { setShowForm(false); setCopySource(null); }} onCreated={() => { setShowForm(false); setCopySource(null); load(); }} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>Email Campaigns</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Create, send, pause and track promotional emails.</Typography>
          </Box>
          <Button variant="contained" startIcon={<MdAdd />} onClick={() => { setCopySource(null); setShowForm(true); }} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>
            New Campaign
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} onClick={() => setStatusFilter(k.filter)} sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: statusFilter === k.filter && k.filter ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, subject or campaign ID" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }} InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          {(search || statusFilter) && <Button onClick={() => { setSearch(''); setStatusFilter(''); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0' } }}>
                <TableCell>Campaign</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{[200, 220, 150, 60, 90, 150].map((w, j) => <TableCell key={j}><Skeleton width={w} /></TableCell>)}</TableRow>)
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{campaigns.length ? 'No campaigns match your filters.' : 'No campaigns yet. Create your first one.'}</Typography></TableCell></TableRow>
              ) : (
                rows.map((c) => {
                  const canStart = ['draft', 'scheduled', 'pending'].includes(c.status);
                  const canStop = ['running', 'paused', 'scheduled', 'draft', 'pending'].includes(c.status);
                  const canDelete = ['draft', 'cancelled'].includes(c.status);
                  return (
                    <TableRow key={c._id} hover onClick={() => navigate(`/email/${c._id}`)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.5, fontFamily: FONT } }}>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{c.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b' }}>{c.campId ? `${c.campId} · ` : ''}{c.status === 'scheduled' ? `Goes out ${fmtDateTime(c.scheduledAt)}` : `Created ${fmtDateTime(c.createdAt)}`}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.subject}>{c.subject}</Typography></TableCell>
                      <TableCell><Progress stats={c.stats} status={c.status} /></TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5 }}>{c.stats?.sent ? `${Math.round(((c.stats.opened || 0) / c.stats.sent) * 100)}%` : '-'}</Typography>
                      </TableCell>
                      <TableCell><StatusChip status={c.status} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {canStart && <Tooltip title="Start sending now" arrow><span><IconButton size="small" disabled={busy} onClick={() => act(c, 'start')} sx={{ ...iconBtn('#166534', '#dcfce7'), mr: 0.75 }}><MdPlayArrow size={18} /></IconButton></span></Tooltip>}
                        {c.status === 'running' && <Tooltip title="Pause" arrow><span><IconButton size="small" disabled={busy} onClick={() => act(c, 'pause')} sx={{ ...iconBtn('#9a3412', '#ffedd5'), mr: 0.75 }}><MdPause size={18} /></IconButton></span></Tooltip>}
                        {c.status === 'paused' && <Tooltip title="Resume" arrow><span><IconButton size="small" disabled={busy} onClick={() => act(c, 'resume')} sx={{ ...iconBtn('#166534', '#dcfce7'), mr: 0.75 }}><MdPlayArrow size={18} /></IconButton></span></Tooltip>}
                        {canStop && <Tooltip title="Stop" arrow><span><IconButton size="small" disabled={busy} onClick={() => setConfirm({ kind: 'stop', campaign: c })} sx={{ ...iconBtn('#b91c1c', '#fee2e2'), mr: 0.75 }}><MdStop size={18} /></IconButton></span></Tooltip>}
                        <Tooltip title="Duplicate" arrow><IconButton size="small" onClick={() => duplicate(c._id)} sx={{ ...iconBtn('#0369a1', '#e0f2fe'), mr: 0.75 }}><MdContentCopy size={16} /></IconButton></Tooltip>
                        <Tooltip title="Track" arrow><IconButton size="small" onClick={() => navigate(`/email/${c._id}`)} sx={{ ...iconBtn('#4f46e5', 'rgba(79,70,229,.1)'), mr: canDelete ? 0.75 : 0 }}><MdVisibility size={17} /></IconButton></Tooltip>
                        {canDelete && <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => setConfirm({ kind: 'delete', campaign: c })} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton></Tooltip>}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>{confirm?.kind === 'stop' ? 'Stop this campaign?' : 'Delete this campaign?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT }}>
            {confirm?.kind === 'stop'
              ? <>Emails already sent to <b>{confirm?.campaign.name}</b> can't be recalled. Everyone still waiting will <b>not</b> receive it, and the campaign can't be restarted.</>
              : <><b>{confirm?.campaign.name}</b> and its recipient list will be removed.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={doConfirm} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Working…' : confirm?.kind === 'stop' ? 'Yes, stop' : 'Yes, delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Email;
